import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../utils/api";

interface CalibrationBucket {
  timeOfDay: string | null;
  sky: string | null;
  n: number;
  meanErrorF: number | null;
  offsetF: number;
  clamped: boolean;
  applies: boolean;
  dataConfidence: string;
}

interface LoopHealth {
  n: number;
  meanAbsBaselineF: number | null;
  meanAbsCorrectedF: number | null;
  rmseBaselineF: number | null;
  rmseCorrectedF: number | null;
  looSkill: number | null;
}

interface WindDirectionStat {
  direction: string | null;
  conventionVersion: string | null;
  n: number;
  wouldBeOffsetF: number | null;
  applied: boolean;
}

interface WindCalibration {
  enabled: boolean;
  promotedConvention: string | null;
  minObsApply: number;
  minObsTrust: number;
  directions: WindDirectionStat[];
}

interface CalibrationReport {
  buckets: CalibrationBucket[];
  loopHealth: LoopHealth;
  wind: WindCalibration;
  correctionModelVersion: string;
  baselineModelVersion: string;
  minObs: number;
}

// looSkill is a variance ratio — noisy and able to swing wildly (even negative) at
// small n, so only surface it once there's enough data to trust it (matches the
// learner's "high data confidence" bar).
const SKILL_TRUST_N = 20;

const confPill = (c: string): string =>
  c === "high" ? "pill--success" : c === "medium" ? "pill--warning" : "pill--quiet";

const fmt = (v: number | null, suffix = ""): string =>
  v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v}${suffix}`;

const title = (s: string | null): string =>
  !s ? "—" : s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function TrackTempModelHealth() {
  const { teamId } = useParams<{ teamId: string }>();
  const [data, setData] = useState<CalibrationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<CalibrationReport>(`/api/strategy/track-temp/calibration?team_id=${teamId}`)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e?.message || "Failed to load projection accuracy"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const lh = data?.loopHealth;
  const minObs = data?.minObs ?? 5;
  const n = lh?.n ?? 0;
  const skill = lh?.looSkill;
  const skillPct = skill === null || skill === undefined ? null : Math.round(skill * 100);
  const showSkill = n >= SKILL_TRUST_N && skillPct !== null;
  const haveMean = lh?.meanAbsCorrectedF != null;
  // minObs is the per-bucket cutoff; reused here as a conservative "enough total
  // runs to show an average" floor for the overall headline.
  const measured = n >= minObs && haveMean;
  // Distinguish a genuine regression (skill < 0) from no-improvement (skill === 0) —
  // never euphemize "the correction is actively worse" as "yet".
  const skillText =
    skillPct === null
      ? ""
      : skillPct > 0
        ? `${skillPct}% better than raw physics`
        : skillPct === 0
          ? "no better than raw physics yet"
          : "currently behind raw physics";
  const measuredSub =
    `Average error between the projected and actual track temp over ${n} calibrated ` +
    `${n === 1 ? "run" : "runs"}` +
    `${n < SKILL_TRUST_N ? ` · skill-vs-physics shows at ${SKILL_TRUST_N}+ runs` : ""}.`;

  return (
    <div className="page page--wide fade-in">
      <Link to={`/teams/${teamId}/settings`} className="back-link">
        &larr; Back to team settings
      </Link>

      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Track-temperature projection</span>
          </div>
        </div>
        <h1 className="editorial-title">Projection accuracy</h1>
        <p className="editorial-dek">
          How closely the track-temp projection has tracked the actual temperature measured
          from uploaded runs. This is the projection model overall (across all teams), measured
          leave-one-out so it can't flatter itself.
        </p>
      </header>

      {loading ? (
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading projection accuracy</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-title">Could not load projection accuracy</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : !data ? null : (
        <>
          {/* Headline — honest primary state. Cold-start until enough runs accrue. */}
          <div className="card" style={st.block}>
            {n === 0 ? (
              <div style={st.headlineWrap}>
                <div style={st.headlineBig}>Running on physics</div>
                <div style={st.headlineSub}>
                  Not enough runs yet — the projection uses its physics baseline until teams
                  upload runs to calibrate against. Accuracy appears here once runs accrue.
                </div>
              </div>
            ) : !measured ? (
              <div style={st.headlineWrap}>
                <div style={st.headlineBig}>Calibrating · {n}/{minObs} runs</div>
                <div style={st.headlineSub}>
                  Building the first accuracy reading — a few more calibrated runs and the
                  average error shows here.
                </div>
              </div>
            ) : (
              <div style={st.headlineWrap}>
                <div style={st.headlineBig}>
                  Within <span style={st.headlineNum}>±{lh!.meanAbsCorrectedF}°F</span>
                  {showSkill && (
                    <span style={{ ...st.headlineSkill, color: skillPct! > 0 ? "var(--live)" : "var(--flag-red)" }}>
                      {skillText}
                    </span>
                  )}
                </div>
                <div style={st.headlineSub}>{measuredSub}</div>
              </div>
            )}
          </div>

          {/* Technical details — demoted; perpetually sparse, for the curious. */}
          <div className="card" style={st.block}>
            <button
              type="button"
              style={st.detailsToggle}
              onClick={() => setShowDetails((v) => !v)}
              aria-expanded={showDetails}
              aria-controls="projection-accuracy-details"
            >
              {showDetails ? "Hide technical details" : "Technical details"}
              <span style={st.detailsVersions}>
                {data.correctionModelVersion} · {data.baselineModelVersion}
              </span>
            </button>

            {/* Always mounted (display-toggled) so aria-controls always resolves. */}
            <div
              id="projection-accuracy-details"
              style={{ ...st.detailsBody, display: showDetails ? "flex" : "none" }}
            >
                {/* Loop-health numbers */}
                {lh && lh.n > 0 ? (
                  <div style={st.lhGrid}>
                    <Metric label="Calibrated runs" value={String(lh.n)} />
                    <Metric label="Mean |err| corrected" value={fmt(lh.meanAbsCorrectedF, "°F")} />
                    <Metric label="Mean |err| baseline" value={fmt(lh.meanAbsBaselineF, "°F")} />
                    <Metric label="RMSE corrected" value={fmt(lh.rmseCorrectedF, "°F")} />
                    <Metric label="RMSE baseline" value={fmt(lh.rmseBaselineF, "°F")} />
                    <Metric
                      label="Skill vs baseline"
                      value={skillPct === null ? "—" : `${skillPct}%`}
                      accent={skillPct === null ? "muted" : skillPct > 0 ? "live" : "red"}
                    />
                  </div>
                ) : (
                  <div style={st.empty}>
                    No corrected runs yet — the learner backfills as quality runs are uploaded.
                  </div>
                )}

                {/* Per-bucket learned offsets */}
                <div style={st.subHead}>Learned offsets by bucket (time × sky)</div>
                {data.buckets.length === 0 ? (
                  <div style={st.empty}>No observations yet.</div>
                ) : (
                  <table className="table striped" style={st.table}>
                    <thead>
                      <tr>
                        <th style={st.th}>Time</th>
                        <th style={st.th}>Sky</th>
                        <th style={st.thNum}>Runs</th>
                        <th style={st.thNum}>Mean err</th>
                        <th style={st.thNum}>Offset</th>
                        <th style={st.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.buckets.map((b, i) => (
                        <tr key={i}>
                          <td style={st.td}>{title(b.timeOfDay)}</td>
                          <td style={st.td}>{title(b.sky)}</td>
                          <td style={st.tdNum}>{b.n}</td>
                          <td style={st.tdNum}>{fmt(b.meanErrorF, "°F")}</td>
                          <td style={st.tdNum}>{b.applies ? fmt(b.offsetF, "°F") : "—"}</td>
                          <td style={st.td}>
                            {b.applies ? (
                              <span className={`pill ${confPill(b.dataConfidence)}`}>
                                {b.dataConfidence}
                                {b.clamped ? " · clamped" : ""}
                              </span>
                            ) : (
                              <span className="pill pill--quiet">cold start · {b.n}/{data.minObs}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Wind (recorded, not applied) */}
                <div style={st.windHead}>
                  <span style={st.subHeadInline}>Wind direction</span>
                  <span className={`pill ${data.wind.enabled ? "pill--success" : "pill--quiet"}`}>
                    {data.wind.enabled ? "active" : "recorded · not applied"}
                  </span>
                </div>
                <p style={st.windNote}>
                  The directional offset is learned but stays dark until the in-sim wind convention
                  is verified — needs {data.wind.minObsApply}+ runs per direction to apply,{" "}
                  {data.wind.minObsTrust}+ to trust.
                </p>
                {data.wind.directions.length === 0 ? (
                  <div style={st.empty}>No wind-tagged observations yet.</div>
                ) : (
                  <table className="table striped" style={st.table}>
                    <thead>
                      <tr>
                        <th style={st.th}>Direction</th>
                        <th style={st.thNum}>Runs</th>
                        <th style={st.thNum}>Would-be offset</th>
                        <th style={st.th}>Convention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.wind.directions.map((w, i) => (
                        <tr key={i}>
                          <td style={st.td}>{w.direction || "—"}</td>
                          <td style={st.tdNum}>{w.n}</td>
                          <td style={st.tdNum}>{fmt(w.wouldBeOffsetF, "°F")}</td>
                          <td style={st.td}>
                            <span className="pill pill--quiet">{w.conventionVersion || "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  accent = "default",
}: {
  label: string;
  value: string;
  accent?: "default" | "live" | "red" | "muted";
}) {
  const color =
    accent === "live" ? "var(--live)" : accent === "red" ? "var(--flag-red)" : "var(--text-strong)";
  return (
    <div style={st.metric}>
      <span style={st.metricLabel}>{label}</span>
      <span style={{ ...st.metricValue, color }}>{value}</span>
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  block: { marginBottom: "1rem" },
  empty: { fontSize: "0.85rem", color: "var(--text-muted)", padding: "0.5rem 0" },

  headlineWrap: { display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0" },
  headlineBig: {
    fontFamily: "var(--font-display)",
    fontSize: "1.8rem",
    fontWeight: 700,
    lineHeight: 1.2,
    color: "var(--text-strong)",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: "0.6rem",
  },
  headlineNum: { fontVariantNumeric: "tabular-nums" },
  headlineSkill: { fontSize: "1rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" },
  headlineSub: { fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.5, maxWidth: "44rem" },

  detailsToggle: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "var(--live)",
    fontFamily: "var(--font-body)",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  detailsVersions: { color: "var(--text-muted)", fontSize: "0.8125rem", fontWeight: 500, fontVariantNumeric: "tabular-nums" },
  detailsBody: { marginTop: "1rem", display: "flex", flexDirection: "column", gap: "1rem" },
  subHead: { fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-secondary)", marginTop: "0.5rem" },
  subHeadInline: { fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-secondary)" },
  table: { width: "100%", marginTop: "0.25rem" },

  lhGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "1rem",
  },
  metric: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  metricLabel: { fontSize: "0.8125rem", color: "var(--text-secondary)", fontWeight: 500 },
  metricValue: { fontSize: "1.5rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  th: { textAlign: "left", padding: "0.45rem 0.6rem", fontSize: "0.8125rem", color: "var(--text-secondary)", fontWeight: 600 },
  thNum: { textAlign: "right", padding: "0.45rem 0.6rem", fontSize: "0.8125rem", color: "var(--text-secondary)", fontWeight: 600 },
  td: { padding: "0.45rem 0.6rem", fontSize: "0.82rem", color: "var(--text-default)" },
  tdNum: { padding: "0.45rem 0.6rem", fontSize: "0.82rem", textAlign: "right", color: "var(--text-default)", fontVariantNumeric: "tabular-nums" },
  windHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginTop: "0.5rem" },
  windNote: { fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.4rem", lineHeight: 1.5 },
};
